/* generated using openapi-typescript-codegen -- do not edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { LoginRequest } from '../models/LoginRequest';
import type { LogoutRequest } from '../models/LogoutRequest';
import type { PasswordResetConfirm } from '../models/PasswordResetConfirm';
import type { PasswordResetConfirmResponse } from '../models/PasswordResetConfirmResponse';
import type { PasswordResetRequest } from '../models/PasswordResetRequest';
import type { PasswordResetRequestResponse } from '../models/PasswordResetRequestResponse';
import type { RefreshRequest } from '../models/RefreshRequest';
import type { SignupRequest } from '../models/SignupRequest';
import type { SignupResponse } from '../models/SignupResponse';
import type { TokenResponse } from '../models/TokenResponse';
import type { VerifyEmailRequest } from '../models/VerifyEmailRequest';
import type { VerifyEmailResponse } from '../models/VerifyEmailResponse';
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';
export class AuthService {
  /**
   * Exchange credentials for an access + refresh token pair
   * Issue a JWT + refresh token for the credentialed user.
   *
   * Implementation: **T-015**. Per functional spec § "Login": password
   * is verified against argon2 hash, account must be email-verified
   * (else 403 ``email_not_verified``). Wrong email or password collapses
   * to the same 401 ``invalid_credentials`` (no enumeration). JWT TTL
   * is 15 minutes; refresh TTL 30 days rolling.
   *
   * Anti-enumeration timing: the unknown-email branch still runs an
   * argon2 verify against a fixed dummy hash so the wall-clock cost of
   * "unknown email" matches "wrong password". A timing oracle that
   * leaks user existence is exactly what the matched 401 message is
   * designed to avoid.
   * @returns TokenResponse Successful Response
   * @throws ApiError
   */
  public static loginAuthLoginPost({
    requestBody,
  }: {
    requestBody: LoginRequest,
  }): CancelablePromise<TokenResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/auth/login',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        409: `Conflict`,
        422: `Validation Error`,
        429: `Too Many Requests`,
        503: `Service Unavailable`,
      },
    });
  }
  /**
   * Revoke the current refresh token
   * Revoke the active refresh token (idempotent ``204``).
   *
   * Implementation: **T-017**. Per functional spec § "Logout": the
   * access token isn't server-revoked (short-lived; expires within 15
   * minutes), only the refresh token is invalidated server-side via
   * :func:`mirlai_accounts.auth.refresh_tokens.revoke` — which is
   * itself idempotent (unknown / already-revoked tokens are a no-op).
   * Already-logged-out callers still get a clean 204, including the
   * "replay an already-revoked token" case.
   *
   * F-505: the refresh token is read from the ``mirlai_refresh``
   * cookie first; the body field is the migration-window fallback.
   * The response always clears the cookie, regardless of which channel
   * carried the token, so the next request from this browser cannot
   * replay a stale value.
   * @returns void
   * @throws ApiError
   */
  public static logoutAuthLogoutPost({
    requestBody,
  }: {
    requestBody: LogoutRequest,
  }): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/auth/logout',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        409: `Conflict`,
        422: `Validation Error`,
        429: `Too Many Requests`,
        503: `Service Unavailable`,
      },
    });
  }
  /**
   * Consume a reset token and set a new password
   * Validate the token, set the new password, and revoke active sessions.
   *
   * Implementation: **T-018**. Per functional spec § "Confirm password
   * reset": the token is consumed atomically, the password is
   * re-validated against the policy, and *all* refresh tokens for the
   * user are invalidated so other devices fall back to login.
   * @returns PasswordResetConfirmResponse Successful Response
   * @throws ApiError
   */
  public static confirmResetAuthPasswordResetConfirmPost({
    requestBody,
  }: {
    requestBody: PasswordResetConfirm,
  }): CancelablePromise<PasswordResetConfirmResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/auth/password-reset/confirm',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        422: `Validation Error`,
        429: `Too Many Requests`,
        503: `Service Unavailable`,
      },
    });
  }
  /**
   * Email a single-use password-reset token
   * Queue a reset email if the address is registered.
   *
   * Implementation: **T-018**. Per functional spec § "Request password
   * reset": always returns 202 with a generic message to prevent
   * account enumeration. The token has a 30-minute TTL and is delivered
   * via a link to ``https://app.mirlai.com/reset-password?token=...``.
   * @returns PasswordResetRequestResponse Successful Response
   * @throws ApiError
   */
  public static requestResetAuthPasswordResetRequestPost({
    requestBody,
  }: {
    requestBody: PasswordResetRequest,
  }): CancelablePromise<PasswordResetRequestResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/auth/password-reset/request',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        422: `Validation Error`,
        429: `Too Many Requests`,
        503: `Service Unavailable`,
      },
    });
  }
  /**
   * Rotate refresh token + issue a new access token
   * Rotate the refresh token and issue a fresh JWT.
   *
   * Per functional spec § "Refresh access token" + technical spec
   * § "Refresh-token reuse detection": each successful refresh revokes
   * the prior token and issues a new one with ``parent_id`` linking back
   * so the rotation chain is preserved. Re-using a revoked token (i.e.
   * replay after the legit user already rotated past it) triggers a
   * chain-wide invalidation and forces the user to log in again
   * everywhere.
   *
   * All failure modes collapse to the SAME ``invalid_or_expired_token``
   * code to avoid telling an attacker whether their replay was detected.
   * The reuse-detection signal is logged loudly server-side via a
   * ``logger.warning`` carrying the user_id — F-204's audit pipeline can
   * pick this up later for "you may have been compromised" UX.
   * @returns TokenResponse Successful Response
   * @throws ApiError
   */
  public static refreshAuthRefreshPost({
    requestBody,
  }: {
    requestBody: RefreshRequest,
  }): CancelablePromise<TokenResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/auth/refresh',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        409: `Conflict`,
        422: `Validation Error`,
        429: `Too Many Requests`,
        503: `Service Unavailable`,
      },
    });
  }
  /**
   * Create a Mirlai cloud account
   * Create a Mirlai cloud account and queue the verification email.
   *
   * Implementation: **T-013**. Per functional spec § "Signup": the email
   * is normalised, password validated against the policy, and a 7-day
   * verification token is mailed to the user. Until they click the
   * verification link, ``/auth/login`` returns 403 ``email_not_verified``.
   * Duplicate email → 409 ``email_taken``; weak password → 400
   * ``weak_password`` with a ``reasons`` array in ``details``.
   * @returns SignupResponse Successful Response
   * @throws ApiError
   */
  public static signupAuthSignupPost({
    requestBody,
  }: {
    requestBody: SignupRequest,
  }): CancelablePromise<SignupResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/auth/signup',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        409: `Conflict`,
        422: `Validation Error`,
        429: `Too Many Requests`,
        503: `Service Unavailable`,
      },
    });
  }
  /**
   * Consume an email-verification token
   * Mark the account's email as verified.
   *
   * Implementation: **T-014**. Per functional spec § "Verify email":
   * verification tokens have a 7-day TTL, are single-use, and verifying
   * an already-verified account is idempotent (still returns 200).
   * Expired / unknown / consumed tokens → 400
   * ``invalid_or_expired_token``.
   * @returns VerifyEmailResponse Successful Response
   * @throws ApiError
   */
  public static verifyEmailAuthVerifyEmailPost({
    requestBody,
  }: {
    requestBody: VerifyEmailRequest,
  }): CancelablePromise<VerifyEmailResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/auth/verify-email',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        422: `Validation Error`,
        429: `Too Many Requests`,
        503: `Service Unavailable`,
      },
    });
  }
}
